import { Box, Flex, NumberInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import useLang from 'lang';
import '@mantine/dates/styles.css';

interface EnergyConsumptionFiltersProps {
  /** Currently selected date */
  selectedDate: Date | null;
  /** Called when the user changes the date */
  setSelectedDate: (date: Date | null) => void;
  /** Fixed tariff in €/MWh */
  fixedTariff: number;
  /** Called when the user changes the fixed tariff */
  setFixedTariff: (value: number) => void;
}

/**
 * Date picker and fixed tariff input controls for the energy consumption page.
 *
 * @prop {Date | null} selectedDate - Currently selected date.
 * @prop {function} setSelectedDate - Callback to update the selected date.
 * @prop {number} fixedTariff - Fixed tariff value in €/MWh.
 * @prop {function} setFixedTariff - Callback to update the fixed tariff.
 */
export default function EnergyConsumptionFilters({
  selectedDate,
  setSelectedDate,
  fixedTariff,
  setFixedTariff,
}: EnergyConsumptionFiltersProps) {
  const { _t } = useLang();

  return (
    <Flex gap={20} mt={10} mb={20} wrap="wrap">
      <Box style={{ maxWidth: 300 }}>
        <DatePickerInput
          label={_t('Select day')}
          value={selectedDate}
          onChange={setSelectedDate}
          clearable
          valueFormat="DD.MM.YYYY"
          maxDate={new Date()}
          description={_t('Select the day for which you want to display consumption')}
          styles={{ input: { fontSize: '16px' } }}
        />
      </Box>
      <Box style={{ maxWidth: 300 }}>
        <NumberInput
          label={_t('Fixed tariff (€/MWh)')}
          value={fixedTariff}
          onChange={(value) => setFixedTariff(typeof value === 'number' ? value : 200)}
          min={0}
          max={1000}
          step={10}
          decimalScale={2}
          placeholder="200"
          description={_t('Set the fixed tariff price')}
        />
      </Box>
    </Flex>
  );
}
